import React from 'react';
import { FileText } from 'lucide-react';
import { getCategoryEnhancedDescription } from '../data/categoryDescriptions';
import type { BlogCategoryId } from '../blog/categories';

interface CategoryIntroProps {
  categoryId: BlogCategoryId;
  locale: string;
}

/**
 * CategoryIntro Component
 *
 * Renders enhanced SEO introduction for category pages
 * Includes localized 150-250 word description
 * SSR-friendly, no client-side dependencies
 */
export const CategoryIntro: React.FC<CategoryIntroProps> = ({ categoryId, locale }) => {
  const enhanced = getCategoryEnhancedDescription(categoryId);

  if (!enhanced) {
    return null;
  }

  // Get localized content with fallback
  const localeKey = locale as keyof typeof enhanced.seoDescription;
  const description = enhanced.seoDescription[localeKey] || enhanced.seoDescription.en;
  const h1 = enhanced.seoH1[localeKey] || enhanced.seoH1.en;

  // Get how-to highlights if available
  const howToHighlights = enhanced.howToHighlights || [];

  return (
    <div className="mb-12">
      {/* Enhanced H1 */}
      <h1 className="text-3xl md:text-4xl font-bold text-charcoal-900 dark:text-white mb-6">
        {h1}
      </h1>

      {/* SEO-optimized description */}
      <div className="bg-gradient-to-br from-neutral-50 to-neutral-100 dark:from-charcoal-800 dark:to-charcoal-700/50 rounded-xl p-6 md:p-8 border border-neutral-200 dark:border-charcoal-600 mb-8">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 mt-1">
            <div className="w-12 h-12 rounded-lg bg-accent-100 dark:bg-accent-900/30 flex items-center justify-center">
              <FileText className="w-6 h-6 text-accent-600 dark:text-accent-400" />
            </div>
          </div>
          <div className="flex-1">
            <p className="text-base leading-relaxed text-neutral-700 dark:text-neutral-300">
              {description}
            </p>
          </div>
        </div>
      </div>

      {/* How-To Highlights (if available) */}
      {howToHighlights.length > 0 && (
        <div className="bg-white dark:bg-charcoal-800 rounded-lg p-6 border border-neutral-200 dark:border-charcoal-700 mb-8">
          <h2 className="text-xl font-bold text-charcoal-900 dark:text-white mb-4 flex items-center gap-2">
            <span className="text-accent-600 dark:text-accent-400">📚</span>
            {locale === 'de' && 'Schritt-für-Schritt-Anleitungen'}
            {locale === 'pt-BR' && 'Guias Passo a Passo em Destaque'}
            {locale === 'ms-MY' && 'Panduan Langkah demi Langkah'}
            {locale === 'es' && 'Guías Paso a Paso Destacadas'}
            {locale === 'fr' && 'Guides Pratiques Étape par Étape'}
            {locale === 'ja' && '実践ステップガイド'}
            {locale === 'id' && 'Panduan Praktis Langkah demi Langkah'}
            {(locale === 'en' || !['de', 'pt-BR', 'ms-MY', 'es', 'fr', 'ja', 'id'].includes(locale)) && 'Step-by-Step Guides'}
          </h2>
          <div className="space-y-3">
            {howToHighlights.map((highlight) => {
              const summary = highlight.summary[localeKey] || highlight.summary.en;
              return (
                <div
                  key={highlight.slug}
                  className="flex items-start gap-3 p-3 rounded-lg hover:bg-neutral-50 dark:hover:bg-charcoal-700/50 transition-colors"
                >
                  <span className="text-accent-600 dark:text-accent-400 font-bold text-sm mt-0.5">→</span>
                  <p className="text-sm text-neutral-700 dark:text-neutral-300 flex-1">
                    {summary}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryIntro;
